import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communicationsApi, type CreateLetterBody } from '../../api/communications';
import { useRole } from '../../hooks/useCan';
import LetterheadForm from './LetterheadForm';
import type {
  Communication,
  Letterhead,
  LetterRecipient,
  LetterTemplateDef,
  LetterTemplateVariableDef,
  PropertyAddressCandidate,
} from '../../types/index';

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

interface LetterWizardModalProps {
  entityType: string;
  entityId: number;
  /** Edit mode: load the existing communication's variables. */
  existing?: Communication | null;
  onClose: () => void;
  onCreated?: (communication: Communication) => void;
}

const EMPTY_RECIPIENT: LetterRecipient = {
  anrede: '',
  name: '',
  strasse: '',
  hausnummer: '',
  plz: '',
  ort: '',
  land: '',
};

const ANREDE_DATALIST_ID = 'wizard-anrede-options';
const ANREDE_OPTIONS = ['Herr', 'Frau', 'Firma'];

function defaultsFromTemplate(tpl: LetterTemplateDef | null): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!tpl) return out;
  for (const v of tpl.variables) {
    out[v.key] = v.default ?? (v.type === 'checklist' ? [] : v.type === 'number' ? 0 : '');
  }
  return out;
}

/** Parse the variables_json envelope from an existing Communication. */
function parseEnvelope(comm: Communication): {
  variables: Record<string, unknown>;
  recipient: LetterRecipient;
  property_adresse_id: number | null;
} | null {
  if (!comm.variables_json) return null;
  try {
    const env = JSON.parse(comm.variables_json);
    if (env && typeof env === 'object' && 'variables' in env) {
      return {
        variables: env.variables || {},
        recipient: { ...EMPTY_RECIPIENT, ...(env.recipient || {}) },
        property_adresse_id: env.property_adresse_id ?? null,
      };
    }
  } catch {
    /* fall through */
  }
  return null;
}

export default function LetterWizardModal({
  entityType,
  entityId,
  existing = null,
  onClose,
  onCreated,
}: LetterWizardModalProps) {
  const queryClient = useQueryClient();
  const role = useRole();
  const canManageCityDefault = role === 'system_admin' || role === 'city_admin';
  const isEdit = !!existing;

  // ---- State (initialized from `existing` envelope when in edit mode) ----
  const envelope = existing ? parseEnvelope(existing) : null;

  const [selectedTemplate, setSelectedTemplate] = useState<string>(existing?.template_name ?? '');
  const [selectedLetterhead, setSelectedLetterhead] = useState<number | null>(
    existing?.letterhead_id ?? null,
  );
  const [showLetterheadCreator, setShowLetterheadCreator] = useState(false);
  const [recipient, setRecipient] = useState<LetterRecipient>(envelope?.recipient ?? EMPTY_RECIPIENT);
  const [propertyAdresseId, setPropertyAdresseId] = useState<number | null>(
    envelope?.property_adresse_id ?? null,
  );
  const [variables, setVariables] = useState<Record<string, unknown>>(envelope?.variables ?? {});
  const [error, setError] = useState<string | null>(null);

  // Track which text/textarea input is currently focused (for chip insertion).
  const focusedVarKeyRef = useRef<string | null>(null);
  const focusedRefMap = useRef<Map<string, HTMLInputElement | HTMLTextAreaElement>>(new Map());

  // ---- Data queries ----
  const { data: templates = [] } = useQuery({
    queryKey: ['letter-templates'],
    queryFn: () => communicationsApi.getLetterTemplates(),
  });

  const { data: letterheads = [] } = useQuery({
    queryKey: ['letterheads'],
    queryFn: () => communicationsApi.getLetterheads(),
  });

  const { data: prefill } = useQuery({
    queryKey: ['communications-prefill', entityType, entityId],
    queryFn: () => communicationsApi.getPrefill(entityType, entityId),
  });

  // ---- Default selections on first load (create mode only) ----
  useEffect(() => {
    if (!isEdit && templates.length && !selectedTemplate) {
      setSelectedTemplate(templates[0].name);
    }
  }, [isEdit, templates, selectedTemplate]);

  useEffect(() => {
    if (!isEdit && letterheads.length && selectedLetterhead === null) {
      setSelectedLetterhead(letterheads[0].id);
    }
  }, [isEdit, letterheads, selectedLetterhead]);

  const currentTemplate = useMemo(
    () => templates.find((t) => t.name === selectedTemplate) ?? null,
    [templates, selectedTemplate],
  );

  // Reset variables to template defaults when the *user* switches template
  // (only in create mode; edit mode keeps the saved variables).
  useEffect(() => {
    if (isEdit) return;
    if (!currentTemplate) return;
    setVariables((prev) => {
      // If we already have values for this template's keys, keep them; else seed defaults.
      const seeded = defaultsFromTemplate(currentTemplate);
      const merged: Record<string, unknown> = { ...seeded };
      for (const v of currentTemplate.variables) {
        if (prev[v.key] !== undefined) merged[v.key] = prev[v.key];
      }
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate?.name, isEdit]);

  // Auto-pick the only property-address candidate (create mode).
  useEffect(() => {
    if (isEdit) return;
    if (propertyAdresseId !== null) return;
    if (prefill && prefill.property_address_candidates.length === 1) {
      setPropertyAdresseId(prefill.property_address_candidates[0].adresse_id);
    }
  }, [isEdit, prefill, propertyAdresseId]);

  // ---- Mutations ----
  const createLetterheadMut = useMutation({
    mutationFn: communicationsApi.createLetterhead,
    onSuccess: (created: Letterhead) => {
      queryClient.invalidateQueries({ queryKey: ['letterheads'] });
      setSelectedLetterhead(created.id);
      setShowLetterheadCreator(false);
    },
  });

  const buildBody = (): CreateLetterBody => ({
    template_name: selectedTemplate,
    letterhead_id: selectedLetterhead as number,
    recipient,
    variables,
    property_adresse_id: propertyAdresseId,
  });

  const submitMut = useMutation({
    mutationFn: () => {
      if (isEdit) return communicationsApi.updateLetterDraft(existing!.id, buildBody());
      return communicationsApi.createLetter(entityType, entityId, buildBody());
    },
    onSuccess: (comm) => {
      queryClient.invalidateQueries({ queryKey: ['communications', entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ['documents', entityType, entityId] });
      onCreated?.(comm);
      onClose();
    },
    onError: () => setError('Brief konnte nicht gespeichert werden. Bitte Eingaben prüfen.'),
  });

  // ---- Insertion chips ----
  const chipValues = useMemo(() => {
    if (!prefill) return [];
    const items: { label: string; value: string }[] = [];
    const firstAddr =
      prefill.property_address_candidates.find((p) => p.adresse_id === propertyAdresseId) ??
      prefill.property_address_candidates[0];
    if (firstAddr) items.push({ label: 'FeWo-Adresse', value: firstAddr.adresse_oneline });
    if (prefill.listing?.registration_number)
      items.push({ label: 'Registriernummer', value: prefill.listing.registration_number });
    if (prefill.listing?.platform)
      items.push({ label: 'Plattform', value: prefill.listing.platform });
    if (prefill.listing?.listing_url)
      items.push({ label: 'Inserat-URL', value: prefill.listing.listing_url });
    if (prefill.listing?.host_name)
      items.push({ label: 'Host', value: prefill.listing.host_name });
    return items;
  }, [prefill, propertyAdresseId]);

  const insertChipIntoVariable = (chipValue: string) => {
    const key = focusedVarKeyRef.current;
    if (!key) return;
    const el = focusedRefMap.current.get(key);
    const current = String(variables[key] ?? '');
    if (el && (el.selectionStart !== null)) {
      const start = el.selectionStart ?? current.length;
      const end = el.selectionEnd ?? current.length;
      const next = current.slice(0, start) + chipValue + current.slice(end);
      setVariables({ ...variables, [key]: next });
      // Restore cursor after the inserted text on next tick
      requestAnimationFrame(() => {
        el.focus();
        const caret = start + chipValue.length;
        el.setSelectionRange(caret, caret);
      });
    } else {
      setVariables({ ...variables, [key]: current + chipValue });
    }
  };

  // ---- Adopt prefill into form ----
  const adoptRecipient = (cand: LetterRecipient) => setRecipient({ ...EMPTY_RECIPIENT, ...cand });
  const adoptPropertyAddress = (cand: PropertyAddressCandidate) =>
    setPropertyAdresseId(cand.adresse_id);

  const canSubmit =
    !!selectedTemplate &&
    selectedLetterhead !== null &&
    !submitMut.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-full flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-medium">
            {isEdit ? 'Brief-Entwurf bearbeiten' : 'Brief erstellen'}
          </h3>
          <button onClick={onClose} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-sm">
            Schließen
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-5">
          {/* Daten zum Vorgang */}
          {prefill && (
            prefill.recipient_candidates.length > 0 ||
            prefill.property_address_candidates.length > 0 ||
            prefill.listing
          ) && (
            <section className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Daten zum Vorgang
              </h4>
              {prefill.recipient_candidates.length > 0 && (
                <div className="text-sm">
                  <span className="text-xs text-gray-500">Vermieter: </span>
                  <span className="text-gray-900">
                    {prefill.recipient_candidates[0].anrede
                      ? prefill.recipient_candidates[0].anrede + ' '
                      : ''}
                    {prefill.recipient_candidates[0].name || '(ohne Namen)'}
                    {prefill.recipient_candidates[0].strasse && (
                      <>
                        {', '}
                        {prefill.recipient_candidates[0].strasse}
                        {prefill.recipient_candidates[0].hausnummer
                          ? ' ' + prefill.recipient_candidates[0].hausnummer
                          : ''}
                        {prefill.recipient_candidates[0].plz || prefill.recipient_candidates[0].ort
                          ? `, ${prefill.recipient_candidates[0].plz ?? ''} ${prefill.recipient_candidates[0].ort ?? ''}`
                          : ''}
                      </>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => adoptRecipient(prefill.recipient_candidates[0])}
                    className="btn btn-text ml-2 text-xs"
                  >
                    In Empfänger übernehmen
                  </button>
                </div>
              )}
              {prefill.property_address_candidates.length > 0 && (
                <div className="text-sm">
                  <span className="text-xs text-gray-500">FeWo-Adresse: </span>
                  {prefill.property_address_candidates.length === 1 ? (
                    <span className="text-gray-900">
                      {prefill.property_address_candidates[0].adresse_oneline}
                    </span>
                  ) : (
                    <select
                      value={propertyAdresseId ?? ''}
                      onChange={(e) =>
                        setPropertyAdresseId(e.target.value ? Number(e.target.value) : null)
                      }
                      className="border border-gray-300 rounded px-2 py-0.5 text-sm"
                    >
                      <option value="">— Bitte wählen —</option>
                      {prefill.property_address_candidates.map((c) => (
                        <option key={c.adresse_id} value={c.adresse_id}>
                          {c.source}: {c.adresse_oneline}
                        </option>
                      ))}
                    </select>
                  )}
                  {prefill.property_address_candidates.length === 1 && propertyAdresseId === null && (
                    <button
                      type="button"
                      onClick={() => adoptPropertyAddress(prefill.property_address_candidates[0])}
                      className="btn btn-text ml-2 text-xs"
                    >
                      Auswählen
                    </button>
                  )}
                </div>
              )}
              {prefill.listing && (
                <div className="text-xs text-gray-600 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  {prefill.listing.title && <div><span className="text-gray-500">Titel:</span> {prefill.listing.title}</div>}
                  {prefill.listing.platform && <div><span className="text-gray-500">Plattform:</span> {prefill.listing.platform}</div>}
                  {prefill.listing.registration_number && <div><span className="text-gray-500">Registriernummer:</span> {prefill.listing.registration_number}</div>}
                  {prefill.listing.host_name && <div><span className="text-gray-500">Gastgeber:</span> {prefill.listing.host_name}</div>}
                  {prefill.listing.listing_url && (
                    <div className="col-span-2 truncate">
                      <span className="text-gray-500">Inserat:</span>{' '}
                      <a href={prefill.listing.listing_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        {prefill.listing.listing_url}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* 1. Template */}
          <section>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Vorlage</h4>
            <div className="space-y-2">
              {templates.map((t) => (
                <label
                  key={t.name}
                  className={`flex items-start gap-2 p-2 border rounded-md cursor-pointer ${
                    selectedTemplate === t.name ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    checked={selectedTemplate === t.name}
                    onChange={() => setSelectedTemplate(t.name)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{t.display_name}</div>
                    {t.description && (
                      <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </section>

          {/* 2. Letterhead */}
          <section>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Briefkopf</h4>
            {!showLetterheadCreator ? (
              <div className="flex items-center gap-2">
                <select
                  value={selectedLetterhead ?? ''}
                  onChange={(e) => setSelectedLetterhead(Number(e.target.value))}
                  className={INPUT_CLASS}
                >
                  {letterheads.length === 0 && <option value="">Keine Briefköpfe vorhanden</option>}
                  {letterheads.map((lh) => (
                    <option key={lh.id} value={lh.id}>
                      {lh.name} {lh.is_city_default ? ' — City-Standard' : ' — Eigener'}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setShowLetterheadCreator(true)}
                  className="btn btn-text whitespace-nowrap text-sm"
                >
                  + Neu
                </button>
              </div>
            ) : (
              <div className="border rounded-md p-3 bg-gray-50">
                <LetterheadForm
                  allowCityDefaultToggle={canManageCityDefault}
                  onSubmit={(body) => createLetterheadMut.mutateAsync(body)}
                  onCancel={() => setShowLetterheadCreator(false)}
                  isSubmitting={createLetterheadMut.isPending}
                  submitLabel="Briefkopf anlegen"
                />
              </div>
            )}
          </section>

          {/* 3. Recipient */}
          <section>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Empfänger</h4>
            <datalist id={ANREDE_DATALIST_ID}>
              {ANREDE_OPTIONS.map((a) => (
                <option key={a} value={a} />
              ))}
            </datalist>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  value={recipient.anrede ?? ''}
                  onChange={(e) => setRecipient({ ...recipient, anrede: e.target.value })}
                  list={ANREDE_DATALIST_ID}
                  className={`${INPUT_CLASS} w-36`}
                  placeholder="Anrede"
                />
                <input
                  value={recipient.name}
                  onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                  className={`${INPUT_CLASS} flex-1`}
                  placeholder="Name"
                />
              </div>
              <div className="flex gap-2">
                <input
                  value={recipient.strasse}
                  onChange={(e) => setRecipient({ ...recipient, strasse: e.target.value })}
                  className={`${INPUT_CLASS} flex-1`}
                  placeholder="Straße"
                />
                <input
                  value={recipient.hausnummer}
                  onChange={(e) => setRecipient({ ...recipient, hausnummer: e.target.value })}
                  className={`${INPUT_CLASS} w-24`}
                  placeholder="Nr."
                />
              </div>
              <div className="flex gap-2">
                <input
                  value={recipient.plz}
                  onChange={(e) => setRecipient({ ...recipient, plz: e.target.value })}
                  className={`${INPUT_CLASS} w-24`}
                  placeholder="PLZ"
                />
                <input
                  value={recipient.ort}
                  onChange={(e) => setRecipient({ ...recipient, ort: e.target.value })}
                  className={`${INPUT_CLASS} flex-1`}
                  placeholder="Ort"
                />
              </div>
              <input
                value={recipient.land}
                onChange={(e) => setRecipient({ ...recipient, land: e.target.value })}
                className={INPUT_CLASS}
                placeholder="Land (leer = Deutschland)"
              />
            </div>
          </section>

          {/* 4. Variables */}
          {currentTemplate && (
            <section>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Inhalt</h4>
              <div className="space-y-3">
                {currentTemplate.variables.map((v) => (
                  <VariableField
                    key={v.key}
                    def={v}
                    value={variables[v.key]}
                    onChange={(val) => setVariables({ ...variables, [v.key]: val })}
                    onFocus={() => {
                      focusedVarKeyRef.current = v.key;
                    }}
                    registerRef={(el) => {
                      if (el) focusedRefMap.current.set(v.key, el);
                      else focusedRefMap.current.delete(v.key);
                    }}
                    chips={chipValues}
                    onInsertChip={insertChipIntoVariable}
                  />
                ))}
              </div>
            </section>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-sm">
            Abbrechen
          </button>
          <button
            onClick={() => submitMut.mutate()}
            disabled={!canSubmit}
            className="btn btn-primary"
          >
            {submitMut.isPending
              ? 'Erzeuge Entwurf…'
              : isEdit
                ? 'Entwurf aktualisieren'
                : 'Entwurf erzeugen'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface VariableFieldProps {
  def: LetterTemplateVariableDef;
  value: unknown;
  onChange: (v: unknown) => void;
  onFocus?: () => void;
  registerRef?: (el: HTMLInputElement | HTMLTextAreaElement | null) => void;
  chips: { label: string; value: string }[];
  onInsertChip: (chipValue: string) => void;
}

function VariableField({ def, value, onChange, onFocus, registerRef, chips, onInsertChip }: VariableFieldProps) {
  const showChips = chips.length > 0 && (def.type === 'text' || def.type === 'textarea');

  const renderChipRow = () =>
    showChips ? (
      <div className="flex flex-wrap gap-1 mt-1">
        {chips.map((c) => (
          <button
            key={c.label}
            type="button"
            onMouseDown={(e) => {
              // prevent textarea/input from losing focus before insertion
              e.preventDefault();
              onInsertChip(c.value);
            }}
            className="text-xs px-2 py-0.5 bg-gray-100 hover:bg-blue-100 text-gray-700 border border-gray-200 rounded"
            title={c.value}
          >
            + {c.label}
          </button>
        ))}
      </div>
    ) : null;

  switch (def.type) {
    case 'text':
      return (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{def.label}</label>
          <input
            ref={(el) => registerRef?.(el)}
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            className={INPUT_CLASS}
          />
          {renderChipRow()}
        </div>
      );
    case 'number':
      return (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{def.label}</label>
          <input
            type="number"
            value={String(value ?? '')}
            onChange={(e) => onChange(Number(e.target.value))}
            className={INPUT_CLASS}
          />
        </div>
      );
    case 'textarea':
      return (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{def.label}</label>
          <textarea
            ref={(el) => registerRef?.(el)}
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            className={INPUT_CLASS}
            rows={3}
          />
          {renderChipRow()}
        </div>
      );
    case 'checklist': {
      const selected: string[] = Array.isArray(value) ? (value as string[]) : [];
      const options = def.options ?? [];
      const toggle = (opt: string) => {
        if (selected.includes(opt)) onChange(selected.filter((s) => s !== opt));
        else onChange([...selected, opt]);
      };
      return (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{def.label}</label>
          <div className="grid grid-cols-2 gap-1 text-sm">
            {options.map((opt) => (
              <label key={opt} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                <span className="text-gray-700">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
}
